// src/components/principal/PrincipalSetupWizard.jsx (v9 – hook order fixed, doublequotes)

import React, { useEffect, useState, useRef, useMemo } from "react";
import api from "../../services/api";
import { API_PATHS } from "../../config";
import MobileAppOnboardingCard from "../MobileAppOnboardingCard";
import WebsiteApiKey from "./WebsiteApiKey";

/**
 * 6-step onboarding wizard for new principals.
 *
 * Guides through display name, school hashtag, mobile app install,
 * optional Instagram connect, optional staff test invite, and website setup.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.profile - Current principal profile data
 * @param {function} props.onProfileChange - Callback to update parent profile state
 * @param {function} props.onError - Error reporting callback
 * @param {number} [props.initialStep=1] - Starting step
 * @param {function} props.onComplete - Called when wizard is finished
 * @param {boolean} [props.visible=true] - Show/hide wizard
 * @param {function} [props.onProfileRefresh] - Optional: refresh mapped profile (same function used by WebsiteApiKey)
 * @returns {JSX.Element|null}
 */
const PrincipalSetupWizard = ({
  profile,
  onProfileChange,
  onError,
  initialStep = 1,
  onComplete,
  visible = true,
  onProfileRefresh, // ✅ pass through from PrincipalDashboard if you have it
}) => {
  // --- Derived examples ----------------------------------------------------
  const rawPrincipalName =
    (profile.principalName ||
      profile.displayName ||
      (profile.email ? profile.email.split("@")[0] : "") ||
      "").trim();

  const principalSurname = rawPrincipalName
    ? rawPrincipalName.split(/\s+/).pop()
    : "Murphy";

  const schoolNameExample = profile.schoolName || "Your School";
  const surnameExample = principalSurname || "Murphy";

  const rawSchoolName = (profile.schoolName || "").trim();
  let schoolHashtagExample = "#YourSchoolName";
  if (rawSchoolName) {
    const camel = rawSchoolName
      .replace(/[^A-Za-z0-9\s]+/g, "")
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join("");
    if (camel) schoolHashtagExample = `#${camel}`;
  }

  // Track whether user started typing hashtag (avoid overwriting local input)
  const userEditedHashtagRef = useRef(false);

  // --- Local wizard state --------------------------------------------------
  const [step, setStep] = useState(initialStep);

  const [displayName, setDisplayName] = useState(
    profile.displayName || (profile.email ? profile.email.split("@")[0] : "")
  );

  const [schoolHashtag, setSchoolHashtag] = useState(
    profile.schoolHashtag ||
      (schoolHashtagExample !== "#YourSchoolName" ? schoolHashtagExample : "")
  );

  const [busy, setBusy] = useState(false);
  const [igBusy, setIgBusy] = useState(false);

  const [testStaffEmail, setTestStaffEmail] = useState("");
  const [testStaffBusy, setTestStaffBusy] = useState(false);
  const [testStaffMessage, setTestStaffMessage] = useState("");

  // Step 6: optional IT email helper
  const [itEmail, setItEmail] = useState("");
  const [itHint, setItHint] = useState("");

  // ✅ Step 6: capture API key from WebsiteApiKey (v20)
  const [wizardApiKey, setWizardApiKey] = useState(null);

  // keep a local schoolWebsite so Step 6 + Website tab stay in sync
  const [localSchoolWebsite, setLocalSchoolWebsite] = useState(
    profile.schoolWebsite || ""
  );

  // ✅ MUST be above any early return (hooks cannot be conditional)
  const isWordPress = useMemo(() => {
    const code = String(profile.cmsPlatformCode || "").toUpperCase();
    return code === "WORDPRESS" || code === "WORDPRESSCOM";
  }, [profile.cmsPlatformCode]);

  // Sync local state with profile changes from parent (no step dependency)
  useEffect(() => {
    setDisplayName(
      profile.displayName || (profile.email ? profile.email.split("@")[0] : "")
    );

    setSchoolHashtag((prev) => {
      if (profile.schoolHashtag) return profile.schoolHashtag;
      if (userEditedHashtagRef.current) return prev;

      const defaultExample =
        schoolHashtagExample !== "#YourSchoolName" ? schoolHashtagExample : "";
      return prev || defaultExample;
    });

    setLocalSchoolWebsite(profile.schoolWebsite || "");
  }, [
    profile.displayName,
    profile.schoolHashtag,
    profile.email,
    profile.schoolWebsite,
    schoolHashtagExample,
  ]);

  // Reset step when initialStep changes
  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  // If profile changes to a non-WP site, clear local key state.
  useEffect(() => {
    if (!isWordPress) setWizardApiKey(null);
  }, [isWordPress]);

  // ✅ Early return AFTER hooks
  if (!visible) return null;

  // --- Helpers -------------------------------------------------------------
  const normalizeHashtag = (value = "") => {
    let normalized = value.trim().replace(/\s+/g, "_");
    if (!normalized) return "";
    return normalized.startsWith("#") ? normalized : `#${normalized}`;
  };

  const advanceTo = (nextStep) => setStep((prev) => Math.max(prev, nextStep));

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

  // Step 1: Save display name
  const handleSaveDisplayName = async (e) => {
    e?.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed) {
      onError?.("Display name cannot be empty.");
      return;
    }

    setBusy(true);
    try {
      await api.post(API_PATHS.UPDATE_DISPLAY_NAME, {
        new_display_name: trimmed,
      });
      onProfileChange?.({ ...profile, displayName: trimmed });
      advanceTo(2);
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Failed to update display name.");
    } finally {
      setBusy(false);
    }
  };

  // Step 2: Save hashtag
  const handleSaveHashtag = async (e) => {
    e?.preventDefault();
    const normalized = normalizeHashtag(schoolHashtag);
    if (!normalized) {
      onError?.("School hashtag cannot be empty.");
      return;
    }

    setBusy(true);
    try {
      await api.post(API_PATHS.UPDATE_SCHOOL_HASHTAG, {
        new_hashtag: normalized,
      });
      onProfileChange?.({ ...profile, schoolHashtag: normalized });
      userEditedHashtagRef.current = false;
      advanceTo(3);
    } catch (err) {
      onError?.(
        err?.response?.data?.detail || "Failed to update school hashtag."
      );
    } finally {
      setBusy(false);
    }
  };

  // Step 3: Mark mobile app installed
  const handleAppDownloaded = async () => {
    try {
      await api.post(API_PATHS.MARK_MOBILE_INSTALLED);
      onProfileChange?.({ ...profile, hasInstalledMobileApp: true });
    } catch (err) {
      console.error("Failed to mark mobile app installed", err);
      onError?.("Couldn't record app install, but you can continue.");
    }
    advanceTo(4);
  };

  // Step 4: Instagram connect
  const handleConnectInstagram = async () => {
    setIgBusy(true);
    try {
      const { data } = await api.get(API_PATHS.INSTAGRAM_LOGIN);
      if (data.url) {
        window.location.href = data.url;
      } else {
        onError?.("Could not start Instagram connection. Try again later.");
      }
    } catch (err) {
      onError?.(
        err?.response?.data?.detail ||
          "Could not connect to Instagram. You can do this later."
      );
    } finally {
      setIgBusy(false);
    }
  };

  // Step 5: Test staff invite
  const handleSendTestStaffInvite = async (e) => {
    e?.preventDefault();
    const email = testStaffEmail.trim();
    if (!email || !validateEmail(email)) {
      onError?.("Please enter a valid email address.");
      return;
    }

    setTestStaffBusy(true);
    try {
      const { data } = await api.post(API_PATHS.CREATE_STAFF, [{ email }]);
      setTestStaffMessage(data?.message || "Test invite sent successfully!");
      setTestStaffEmail("");
    } catch (err) {
      onError?.(
        err?.response?.data?.detail ||
          "Failed to send test invite. You can do this later from Staff Management."
      );
    } finally {
      setTestStaffBusy(false);
    }
  };

  const handleFinish = () => onComplete?.();

  const buildSubject = () =>
    `Please install GhrianGhraffy WordPress plugin for ${
      profile.schoolName || "our school"
    }`;

  const handleOpenEmailApp = () => {
    const email = itEmail.trim();
    if (!email || !validateEmail(email)) {
      onError?.("Please enter a valid IT/support email address (or skip).");
      return;
    }

    if (!wizardApiKey) {
      onError?.("API key is still loading — please wait a moment and try again.");
      return;
    }

    const subject = encodeURIComponent(buildSubject());

    const principalSignoff =
      profile.principalName || profile.displayName || "School Principal";

    const bodyRaw = `Dear Website Administrator,

    Our school is now using the "GhrianGhraffy App" to enhance how we share school photos with parents.

    Please update our school website by installing the "GhrianGhraffy API Photo Display" plugin from the main WordPress plugin directory (search for "GhrianGhraffy").

    Once installed and activated:

    1. Go to Settings → General in WordPress.
    2. Scroll to the "GhrianGhraffy" section.
    3. Paste the API Key below into the "GhrianGhraffy API Key" field.
    4. Click "Connect to GhrianGhraffy".
    5. Confirm the correct school name and School ID appear.
    6. Save changes at the bottom.

    API Key: ${wizardApiKey}

    (If needed, our School ID is: ${profile.schoolId || ""})
    ${localSchoolWebsite ? `Website: ${localSchoolWebsite}\n` : ""}
    This will create a secure "Photos" page on the website.

    Please let me know when it's done.

    Kind regards,
    ${principalSignoff}`;

    const body = encodeURIComponent(bodyRaw);

    window.location.href = `mailto:${encodeURIComponent(
      email
    )}?subject=${subject}&body=${body}`;

    setItHint("Opened your email app with the API key included.");
    setTimeout(() => setItHint(""), 4500);
  };

  // --- Render --------------------------------------------------------------
  return (
    <div className="principal-setup-overlay">
      <div className="principal-setup-modal">
        <div className="principal-setup-header">
          <p className="principal-setup-kicker">First-time setup</p>
          <h2 className="principal-setup-title">
            {principalSurname
              ? `Welcome to GhrianGhraffy, Principal ${principalSurname}!`
              : "Welcome to GhrianGhraffy!"}
          </h2>
          <p className="principal-setup-subtitle">
            Just six quick steps (the last three are optional) and your school
            will be ready.
          </p>
        </div>

        <ol className="principal-setup-steps">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <li
              key={n}
              className={`principal-setup-step ${
                step === n ? "is-active" : step > n ? "is-complete" : ""
              }`}
            >
              <span className="principal-setup-step-number">{n}</span>
              <span className="principal-setup-step-text">
                {n === 1 && "Choose your display name"}
                {n === 2 && "Set your school hashtag"}
                {n === 3 && "Install the mobile app"}
                {n === 4 && "Connect Instagram (optional)"}
                {n === 5 && "Invite a staff member (optional)"}
                {n === 6 && "Get your website ready"}
              </span>
            </li>
          ))}
        </ol>

        {/* Step 1 */}
        {step === 1 && (
          <form
            className="principal-setup-panel"
            onSubmit={handleSaveDisplayName}
          >
            <h3>Choose how your name appears</h3>
            <p className="principal-setup-panel-text">
              Use a friendly name like{" "}
              <strong>“Principal {surnameExample}”</strong> or{" "}
              <strong>“{schoolNameExample} Principal”</strong>.
            </p>
            <label className="principal-setup-label">
              Display name
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={255}
                placeholder={`e.g. Principal ${surnameExample}`}
                className="principal-setup-input"
                required
              />
            </label>
            <div className="principal-setup-actions">
              <button
                type="submit"
                className="principal-setup-primary-btn"
                disabled={busy}
              >
                {busy ? "Saving…" : "Save and continue"}
              </button>
            </div>
          </form>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <form className="principal-setup-panel" onSubmit={handleSaveHashtag}>
            <h3>Create your school hashtag</h3>
            <p className="principal-setup-panel-text">
              We use this to group your photos. Try{" "}
              <strong>{schoolHashtagExample}</strong>.
            </p>
            <label className="principal-setup-label">
              School hashtag
              <input
                type="text"
                value={schoolHashtag}
                onChange={(e) => {
                  userEditedHashtagRef.current = true;
                  setSchoolHashtag(e.target.value);
                }}
                maxLength={50}
                placeholder={schoolHashtagExample}
                className="principal-setup-input"
                required
              />
              <span className="principal-setup-hint">
                Spaces become underscores; we’ll add the # if needed.
              </span>
            </label>
            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(1)}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="submit"
                className="principal-setup-primary-btn"
                disabled={busy}
              >
                {busy ? "Saving…" : "Save and continue"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="principal-setup-panel">
            <h3>Install the GhrianGhraffy app</h3>
            <p className="principal-setup-panel-text">
              This is where you and your staff will take and upload photos.
            </p>
            <MobileAppOnboardingCard
              variant="principal"
              storageKey={null}
              dismissible={false}
              onDownload={handleAppDownloaded}
            />
            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(2)}
              >
                Back
              </button>
              <button
                type="button"
                className="principal-setup-primary-btn"
                onClick={() => advanceTo(4)}
              >
                I’ll install later
              </button>
            </div>
          </div>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="principal-setup-panel">
            <h3>Connect Instagram (optional)</h3>
            <p className="principal-setup-panel-text">
              Link your school Instagram to post approved photos with one tap.
            </p>
            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(3)}
                disabled={igBusy}
              >
                Back
              </button>
              <button
                type="button"
                className="principal-setup-primary-btn"
                onClick={handleConnectInstagram}
                disabled={igBusy}
              >
                {igBusy ? "Connecting…" : "Connect Instagram"}
              </button>
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => advanceTo(5)}
                disabled={igBusy}
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {/* Step 5 */}
        {step === 5 && (
          <div className="principal-setup-panel">
            <h3>Invite a staff member to test (optional)</h3>
            <p className="principal-setup-panel-text">
              Send a test invite to one teacher to try the full workflow.
            </p>
            <form onSubmit={handleSendTestStaffInvite}>
              <input
                type="email"
                value={testStaffEmail}
                onChange={(e) => setTestStaffEmail(e.target.value)}
                placeholder="teacher@example.ie"
                className="principal-setup-input"
                style={{ maxWidth: "260px", marginRight: "0.5rem" }}
                required
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={testStaffBusy}
              >
                {testStaffBusy ? "Sending…" : "Send test invite"}
              </button>
            </form>
            {testStaffMessage && (
              <p className="principal-setup-panel-text small success">
                {testStaffMessage}
              </p>
            )}
            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(4)}
                disabled={testStaffBusy}
              >
                Back
              </button>
              <button
                type="button"
                className="principal-setup-primary-btn"
                onClick={() => advanceTo(6)}
                disabled={testStaffBusy}
              >
                Next step
              </button>
            </div>
          </div>
        )}

        {/* Step 6 */}
        {step === 6 && (
          <div className="principal-setup-panel">
            <h3>Get your school website ready</h3>
            <p className="principal-setup-panel-text">
              Let’s do this now. Confirm your website, we’ll detect WordPress,
              generate an API key (if needed), and you can copy the email to your
              web admin.
            </p>

            <WebsiteApiKey
              cmsPlatformCode={profile.cmsPlatformCode}
              cmsPlatformLabel={profile.cmsPlatformLabel}
              cmsBuilderCode={profile.cmsBuilderCode}
              cmsBuilderLabel={profile.cmsBuilderLabel}
              cmsLastDetectedAt={profile.cmsLastDetectedAt}
              schoolId={profile.schoolId}
              principalName={profile.principalName || profile.displayName}
              onError={onError}
              schoolWebsite={localSchoolWebsite}
              onSchoolWebsiteUpdated={(url) => {
                setLocalSchoolWebsite(url || "");
                onProfileChange?.({ ...profile, schoolWebsite: url || "" });
              }}
              onProfileRefresh={onProfileRefresh}
              onApiKeyReady={(k) => setWizardApiKey(k || null)}
            />

            {isWordPress && (
              <div style={{ marginTop: 14 }}>
                <p
                  className="principal-setup-panel-text small"
                  style={{ marginBottom: 8 }}
                >
                  Optional: enter your IT/support email and we’ll open your email
                  app with everything filled in (including the API key).
                </p>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="email"
                    value={itEmail}
                    onChange={(e) => setItEmail(e.target.value)}
                    placeholder="it-support@example.ie"
                    className="principal-setup-input"
                    style={{ maxWidth: 320 }}
                  />

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleOpenEmailApp}
                    disabled={!itEmail.trim() || !wizardApiKey}
                    title={
                      !wizardApiKey
                        ? "Waiting for API key…"
                        : "Open your email app"
                    }
                  >
                    {!wizardApiKey ? "Loading key…" : "Open email app"}
                  </button>

                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setItEmail("");
                      setItHint(
                        "No problem — you can do this later from your dashboard."
                      );
                      setTimeout(() => setItHint(""), 3500);
                    }}
                  >
                    Skip
                  </button>
                </div>

                {itHint && (
                  <p
                    className="principal-setup-panel-text small"
                    style={{ marginTop: 10 }}
                  >
                    {itHint}
                  </p>
                )}
              </div>
            )}

            <div className="principal-setup-actions principal-setup-actions--final">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(5)}
              >
                Back
              </button>
              <button
                type="button"
                className="principal-setup-primary-btn"
                onClick={handleFinish}
              >
                Finish setup
              </button>
            </div>

            <p
              className="principal-setup-panel-text small"
              style={{ marginTop: "0.6rem", textAlign: "center" }}
            >
              This setup screen won’t appear again. You can always adjust
              settings from your dashboard.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrincipalSetupWizard;
